/**
 * «باقة المنشأة» — what the institution's package gives it, for its owner and
 * administrative manager.
 *
 * The package's name, the modules that are read-only, being closed or on
 * trial, and a meter for every limit with this month's (or today's) use —
 * amber from 80%, the plan's notice. It is the same answer the server
 * enforces, pushed again the moment the Super Admin changes it.
 */

import { Icon, Panel } from '../../components/common';
import { usePermissions } from '../../context/PermissionContext';
import { METRIC_LABELS, STATE_LABELS } from '../../domain/serviceControl';
import { Meter, StatePill } from '../admin/control/controlUi';
import { useI18n } from '../../i18n/I18nProvider';

const MODULE_LABELS = {
  reception: 'استقبال المرضى',
  receipts: 'الإيصالات',
  clinicsDesk: 'استقبال العيادات',
  secretaryDesk: 'سكرتارية الطبيب',
  queue: 'الطابور وشاشة القاعة',
  contracts: 'التعاقدات والموافقات',
  accountsDesk: 'الحسابات',
  rooms: 'الغرف والعمليات',
  nursing: 'التمريض',
  lab: 'المعمل',
  radiology: 'الأشعة',
  pharmacy: 'الصيدلية',
  portal: 'روابط المرضى',
  aiAnalysis: 'التحليل الذكي',
  aiPremium: 'النموذج «الأذكى»',
  onlineDoctors: 'حجز الأطباء أونلاين',
  onlineServices: 'حجز الخدمات أونلاين',
};

export function PackageCard() {
  const { control } = usePermissions();
  const { tr } = useI18n();
  if (!control || !control.plan) return null;
  const entries = Object.entries(control.controls || {});
  const notable = entries.filter(([id, e]) => MODULE_LABELS[id] && ['readonly', 'draining', 'trial', 'closed'].includes(e.state));
  const meters = entries.flatMap(([id, e]) =>
    Object.entries(e.limits || {})
      .filter(([metric]) => METRIC_LABELS[`${id}.${metric}`])
      .map(([metric, row]) => ({ key: `${id}.${metric}`, label: METRIC_LABELS[`${id}.${metric}`], ...row })),
  );
  return (
    <Panel className="package-card mb-6" data-testid="package-card">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="tile3d__face tone-purple shrink-0" style={{ width: 40, height: 40 }}>
          <Icon name="package" size={19} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white">{tr('باقة المنشأة')}</p>
          <p className="text-xs text-slate-400">{tr('اللي مفتوح ليكم وحدوده، من إدارة المنصة. أي تغيير بيوصلكم هنا على طول.')}</p>
        </div>
        <span className="package-card__chip" data-testid="package-name">
          <Icon name="package" size={13} /> {control.plan.name}
        </span>
      </div>

      {notable.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {notable.map(([id, e]) => (
            <span key={id} className="flex items-center gap-1.5 rounded-xl bg-white/5 px-2.5 py-1 text-xs text-slate-200" title={tr(STATE_LABELS[e.state])}>
              {tr(MODULE_LABELS[id])} <StatePill state={e.state} />
            </span>
          ))}
        </div>
      )}

      {meters.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {meters.map((m) => (
            <Meter key={m.key} label={tr(m.label)} used={m.used} limit={m.limit} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-emerald-300">{tr('ما فيش حدود على استخدامكم في الباقة دي.')}</p>
      )}
    </Panel>
  );
}

export default PackageCard;
